import { MODE_VOICE_TOPUP } from "../../supabase/functions/_shared/payment-modes";

export { MODE_VOICE_TOPUP };

export const VOICE_TOPUP_DESCRIPTION = "Flow - Recarga de saldo de voz";

export interface VoiceCreditSummary {
  enrolled: boolean;
  exempt: boolean;
  balanceCop: number;
  priceCop: number | null;
  pendingCop: number;
  legacyMinutesLeft: number;
  canCall: boolean;
  topupMinCop: number | null;
}

export function parseVoiceCreditSummary(raw: unknown): VoiceCreditSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const price = Number(row.price_cop);
  const topupMin = Number(row.topup_min_cop);
  return {
    enrolled: row.enrolled === true,
    exempt: row.exempt === true,
    balanceCop: Number(row.balance_cop) || 0,
    priceCop: Number.isFinite(price) && price > 0 ? price : null,
    pendingCop: Number(row.pending_cop) || 0,
    legacyMinutesLeft: Math.max(0, Number(row.legacy_minutes_left) || 0),
    canCall: row.can_call !== false,
    topupMinCop: row.topup_min_cop !== null && Number.isInteger(topupMin) && topupMin > 0 ? topupMin : null,
  };
}

export function voiceMinutesFor(amountCop: number | null | undefined, priceCop: number | null | undefined): number | null {
  if (amountCop === null || amountCop === undefined || !Number.isFinite(amountCop) || amountCop < 0) return null;
  if (!priceCop || priceCop <= 0) return null;
  return Math.floor(amountCop / priceCop);
}

export function voiceMinutesLabel(amountCop: number | null | undefined, priceCop: number | null | undefined): string | null {
  const minutes = voiceMinutesFor(amountCop, priceCop);
  if (minutes === null || !priceCop) return null;
  return `≈ ${minutes.toLocaleString("es-CO")} min a $${priceCop.toLocaleString("es-CO")}/min`;
}

const VOICE_LEDGER_KIND_LABEL: Record<string, string> = {
  voice_topup: "Recarga de saldo de voz",
  voice_debit: "Llamada",
  voice_adjustment: "Ajuste",
  voice_migration: "Saldo migrado",
};

export function voiceLedgerKindLabel(kind: string): string {
  return VOICE_LEDGER_KIND_LABEL[kind] ?? "Movimiento";
}

export function showVoiceTopup(input: { brandId: string; brandHasVoice: boolean; hasVoiceAccount: boolean }): boolean {
  return input.brandId !== "etb" && input.brandHasVoice && input.hasVoiceAccount;
}

export function feeFactor(raw: number | string | null | undefined): number {
  const value = Number(raw);
  return raw === null || raw === undefined || raw === "" || !Number.isFinite(value) || value < 1 ? 1 : value;
}

export function feeFactorPercent(raw: number | string | null | undefined): string {
  const pct = (1 - 1 / feeFactor(raw)) * 100;
  return `${(Math.round(pct * 100) / 100).toLocaleString("es-CO")} %`;
}

export function voicePriceFloor(
  costUsdPerMin: number,
  trmMax: number | null | undefined,
  margin: number,
  paymentFeeFactor: number | null | undefined = 1,
): number | null {
  if (!Number.isFinite(costUsdPerMin) || costUsdPerMin <= 0) return null;
  const trm = Number(trmMax);
  if (trmMax === null || trmMax === undefined || !Number.isFinite(trm) || trm <= 0) return null;
  if (!Number.isFinite(margin) || margin < 0) return null;
  const fee = feeFactor(paymentFeeFactor);
  const raw = Math.round(costUsdPerMin * trm * (1 + margin) * fee * 1e6) / 1e6;
  return Math.ceil(raw / 10) * 10;
}

export function parseCopInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const value = Number(trimmed.replace(/[$.\s]/g, ""));
  return Number.isInteger(value) && value > 0 ? value : null;
}

export function voiceCallDebitCop(
  costUsd: number | null | undefined,
  trmCopPerUsd: number | null | undefined,
  marginPct: number | null | undefined,
  paymentFeeFactor: number | null | undefined = 1,
): number | null {
  if (costUsd === null || costUsd === undefined || !Number.isFinite(costUsd) || costUsd < 0) return null;
  if (trmCopPerUsd === null || trmCopPerUsd === undefined || !Number.isFinite(trmCopPerUsd) || trmCopPerUsd <= 0) return null;
  if (marginPct === null || marginPct === undefined || !Number.isFinite(marginPct) || marginPct < 0) return null;
  return Math.round(Math.round(costUsd * trmCopPerUsd * (1 + marginPct / 100) * feeFactor(paymentFeeFactor) * 1e6) / 1e6);
}
